import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';

import {
  setMinorAddressSameAsAdult,
  updateHolderRequest,
} from '@/api/operation';

import { LS_USER_BLOCKED_AT_PATH } from '@/constants/common';
import {
  DEFAULT_MINOR_MAILING_FORM_VALUES,
  getMinorProofOfAddressValidationSchema,
  MinorAddressFormType,
} from '@/constants/minor';
import { ROUTE } from '@/constants/routes';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { notifyUserChanged } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectConfigsState,
  selectMailingAddressFeature,
} from '@/redux/configs';
import { notifyMinorDataChanged, selectOperationData } from '@/redux/operation';

import { DocumentStatus, RequiredDocumentSection } from '@/types/document';
import { AddressType } from '@/types/user';

import { getNormalizedMailingAddress } from '@/utils/address';
import { isNonResidentMinorRejectedError } from '@/utils/api';

import {
  buildTinPayload,
  calculateIsDifferentTaxResidency,
  calculateTinVisibility,
  getAdultAddressData,
  getInitialShowDocumentUpload,
  getMailingFormDefaults,
  getMinorAddressData,
  getMinorTaxData,
  getNormalizedTaxResidences,
  hasMailingAddress,
  minorAddressFormToAddressFormForMailing,
} from './helper';

type UseMinorAddressFormProps = {
  open: boolean;
  section: RequiredDocumentSection;
  onClose: () => void;
};

type OnHandleSubmitAddressProps = {
  data: MinorAddressFormType;
  shouldShowUsTin: boolean;
  shouldShowLocalTin: boolean;
  otherCountriesTin: string[];
};

export function useMinorAddressForm({
  open,
  section,
  onClose,
}: UseMinorAddressFormProps) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [, setUserBlockedPath] = useLocalStorage(LS_USER_BLOCKED_AT_PATH, '');
  const queryClient = useQueryClient();
  const adultUser = useAppSelector(selectLoggedInUser);
  const { user: minorUser } = useAppSelector(selectOperationData);
  const {
    requiredLocalTIN,
    isoCode: localISO,
    residenceOptions,
    childLivesWithRelationshipOptions,
  } = useAppSelector(selectConfigsState);
  const shouldAddMailingAddress = useAppSelector(selectMailingAddressFeature);
  const hasChildLivesWithRelationshipOptions =
    !!childLivesWithRelationshipOptions?.length;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(
    getInitialShowDocumentUpload(minorUser),
  );

  const normalizedTaxResidences = useMemo(
    () => getNormalizedTaxResidences(minorUser?.taxResidences),
    [minorUser?.taxResidences],
  );

  const isDifferentTaxResidency = useMemo(
    () =>
      calculateIsDifferentTaxResidency(
        normalizedTaxResidences,
        minorUser?.addressDetailed?.country,
      ),
    [normalizedTaxResidences, minorUser?.addressDetailed?.country],
  );

  const adultPermanentAddress = useMemo(
    () => getAdultAddressData(adultUser),
    [adultUser],
  );

  const adultMailingAddress = useMemo(
    () => getMailingFormDefaults(adultUser),
    [adultUser],
  );

  const minorResidence = useMemo(
    () => getMinorAddressData(minorUser).residence,
    [minorUser],
  );
  const minorResidenceCountry = minorUser?.residenceCountry ?? '';

  const minorTaxData = useMemo(
    () =>
      getMinorTaxData(
        minorUser,
        normalizedTaxResidences,
        isDifferentTaxResidency,
        requiredLocalTIN,
        localISO,
      ),
    [
      minorUser,
      normalizedTaxResidences,
      isDifferentTaxResidency,
      requiredLocalTIN,
      localISO,
    ],
  );

  const validationSchema = useMemo(
    () =>
      getMinorProofOfAddressValidationSchema(t, {
        hasResidenceOptions: !!residenceOptions,
        shouldAddMailingAddress,
        hasChildLivesWithRelationshipOptions,
      }),
    [
      t,
      residenceOptions,
      shouldAddMailingAddress,
      hasChildLivesWithRelationshipOptions,
    ],
  );

  const formValues = useMemo<MinorAddressFormType>(
    () => ({
      shareAddress: true,
      ...adultPermanentAddress,
      ...minorTaxData,
      ...(shouldAddMailingAddress
        ? adultMailingAddress
        : DEFAULT_MINOR_MAILING_FORM_VALUES),
      residence: minorResidence,
      residenceCountry: minorResidenceCountry,
    }),
    [
      adultPermanentAddress,
      minorTaxData,
      shouldAddMailingAddress,
      adultMailingAddress,
      minorResidence,
      minorResidenceCountry,
    ],
  );

  const formMethods = useForm<MinorAddressFormType>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema) as any,
    defaultValues: formValues,
    mode: 'onChange',
  });

  const { reset, handleSubmit, formState } = formMethods;
  const { isValid } = formState;

  useEffect(() => {
    if (open) {
      reset(formValues);
      setShowDocumentUpload(getInitialShowDocumentUpload(minorUser));
    }
  }, [open, formValues, minorUser, reset]);

  const prevSectionStatusRef = useRef(section?.status);

  useEffect(() => {
    const prevStatus = prevSectionStatusRef.current;
    const currentStatus = section?.status;

    if (
      showDocumentUpload &&
      prevStatus === DocumentStatus.Pending &&
      currentStatus &&
      currentStatus !== DocumentStatus.Pending
    ) {
      setShowDocumentUpload(false);
      onClose();
    }

    prevSectionStatusRef.current = currentStatus;
  }, [section?.status, showDocumentUpload, onClose]);

  const onShareAddress = async ({
    data,
    shouldShowUsTin,
    shouldShowLocalTin,
    otherCountriesTin,
  }: OnHandleSubmitAddressProps) => {
    if (!adultUser?.operationId || !minorUser?.id) return;
    await setMinorAddressSameAsAdult({
      operationId: adultUser.operationId,
      minorId: minorUser.id,
    });

    if (shouldAddMailingAddress && hasMailingAddress(adultUser)) {
      await setMinorAddressSameAsAdult({
        operationId: adultUser.operationId,
        minorId: minorUser.id,
        type: AddressType.MAILING,
      });
    }

    const taxResidencesValue = data.taxResidences || [];
    const tin = buildTinPayload({
      data,
      shouldShowUsTin,
      shouldShowLocalTin,
      otherCountriesTin,
      localISO,
    });

    await updateHolderRequest(adultUser.operationId, minorUser.id, {
      ...(data.residence && {
        addressDetailed: {
          residence: data.residence,
        },
      }),
      ...(data.residenceCountry && { residenceCountry: data.residenceCountry }),
      taxResidences: taxResidencesValue.filter(Boolean).join(','),
      ...(Object.keys(tin).length > 0 && { tin }),
    });

    dispatch(notifyUserChanged());
    dispatch(notifyMinorDataChanged());
    await queryClient.invalidateQueries({
      queryKey: ['minor-holder-details-and-required-documents'],
    });
    onClose();
  };

  const onNotShareAddress = async ({
    data,
    shouldShowUsTin,
    shouldShowLocalTin,
    otherCountriesTin,
  }: OnHandleSubmitAddressProps) => {
    if (!adultUser?.operationId || !minorUser?.id) return;
    const addressDetailed = {
      line1: data.line1,
      city: data.city,
      state: data.state,
      zip: data.zip,
      country: data.country,
      ...(data.residence && { residence: data.residence }),
      ...(hasChildLivesWithRelationshipOptions && {
        childLivesWithPersonName: data.childLivesWithPersonName,
        childLivesWithRelationship: data.childLivesWithRelationship,
        childLivesWithRelationshipDescription:
          data.childLivesWithRelationship === 'OTHER'
            ? data.childLivesWithRelationshipDescription
            : undefined,
      }),
    };

    const taxResidencesValue = data.differentTaxResidency
      ? data.taxResidences
      : [data.country];
    const tin = buildTinPayload({
      data,
      shouldShowUsTin,
      shouldShowLocalTin,
      otherCountriesTin,
      localISO,
    });

    let payload: Parameters<typeof updateHolderRequest>[2] = {
      addressDetailed,
      taxResidences: taxResidencesValue.filter(Boolean).join(','),
      ...(Object.keys(tin).length > 0 && { tin }),
      ...(data.residenceCountry && { residenceCountry: data.residenceCountry }),
    };

    if (shouldAddMailingAddress && adultUser.tenant) {
      const mailingAddress = getNormalizedMailingAddress(
        minorAddressFormToAddressFormForMailing(data),
        adultUser.tenant,
        { shouldAddMailingAddress: true },
      );
      if (mailingAddress !== undefined) {
        payload = { ...payload, mailingAddress };
      }
    }

    await updateHolderRequest(adultUser.operationId, minorUser.id, payload);

    dispatch(notifyUserChanged());
    dispatch(notifyMinorDataChanged());
    await queryClient.invalidateQueries({
      queryKey: ['minor-holder-details-and-required-documents'],
    });

    setShowDocumentUpload(true);
  };

  const handleFormSubmit = handleSubmit(async data => {
    if (!adultUser?.operationId || !minorUser?.id) return;

    setIsSubmitting(true);
    try {
      const effectiveDifferentTaxResidency =
        data.shareAddress || data.differentTaxResidency;
      const { shouldShowUsTin, shouldShowLocalTin, otherCountriesTin } =
        calculateTinVisibility({
          effectiveDifferentTaxResidency,
          taxResidences: data.taxResidences,
          country: data.country,
          requiredLocalTIN,
          localISO,
          minorUser,
        });

      if (data.shareAddress) {
        await onShareAddress({
          data,
          shouldShowUsTin,
          shouldShowLocalTin,
          otherCountriesTin,
        });
      } else {
        await onNotShareAddress({
          data,
          shouldShowUsTin,
          shouldShowLocalTin,
          otherCountriesTin,
        });
      }
    } catch (error) {
      if (isNonResidentMinorRejectedError(error)) {
        setUserBlockedPath(ROUTE.NON_RESIDENT_MINOR_REJECTED);
        navigate(ROUTE.NON_RESIDENT_MINOR_REJECTED, { replace: true });
        return;
      }
      console.error('Failed to update minor address:', error);
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleDocumentUploadClose = () => {
    setShowDocumentUpload(false);
    onClose();
  };

  return {
    formMethods,
    isValid,
    isSubmitting,
    shouldAddMailingAddress,
    showDocumentUpload,
    handleFormSubmit,
    handleDocumentUploadClose,
    residenceOptions,
  };
}
